import React from 'react';
import { Award, Shield, Users, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

export const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const features = [
    {
      icon: Award,
      title: 'Established Excellence',
      description: 'Founded in 2015, we bring years of expertise and industry knowledge to every project.',
    },
    {
      icon: Shield,
      title: 'Trusted Leadership',
      description: 'Led by Mr. Varnam Vaheesan (B.Com), ensuring professional and reliable service.',
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'Our commitment to customer satisfaction drives everything we do, every single day.',
    },
    {
      icon: TrendingUp,
      title: 'Continuous Growth',
      description: 'Evolving with market trends to provide cutting-edge solutions for our clients.',
    },
  ];

  return (
    <section ref={ref} className="py-10 sm:py-14 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            About <span className="gradient-text-blue">VTV Enterprises</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Building trust through excellence, innovation, and unwavering commitment to our customers.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="metallic-card p-6 rounded-xl hover-lift group"
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="p-4 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                    <Icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Company Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 glass-card p-8 sm:p-12 rounded-2xl max-w-4xl mx-auto"
        >
          <div className="text-center space-y-8">
            <h3 className="text-2xl sm:text-3xl font-bold">Our Motto</h3>

            {/* Mission */}
            <div className="space-y-3">
              <h4 className="text-xl font-semibold text-primary">Mission</h4>
              <p className="text-muted-foreground leading-relaxed text-base">
                "To deliver innovative solutions and exceptional service that drive success and growth for our clients."
              </p>
            </div>

            {/* Vision */}
            <div className="space-y-3">
              <h4 className="text-xl font-semibold text-accent">Vision</h4>
              <p className="text-muted-foreground leading-relaxed text-base">
                "To be an Island leader in providing versatile, high-quality solutions that create lasting value."
              </p>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-primary" />
                <span className="text-muted-foreground">Founded: November 22, 2015</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-accent" />
                <span className="text-muted-foreground">Chairman: Mr. Varnam Vaheesan</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-primary" />
                <span className="text-muted-foreground">Secretary: Mrs. Thushary Vaheesan</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
